import { Unit } from "@atoms/input/input-unit";
import { ReceivedEInvoices } from "@/features/e-invoicing/types/types";
import { normalizeUnitCode } from "@shared/consts";
import { formatAmount, formatNumber } from "@/features/utils/format/strings";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { Badge } from "@radix-ui/themes";
import { format } from "date-fns";
import _ from "lodash";
import { fr } from "date-fns/locale";

export const ReceivedEInvoiceDetails = ({
  invoice,
}: {
  invoice: ReceivedEInvoices;
}) => {
  const enInvoice = invoice.en_invoice;

  if (!enInvoice) {
    return <div>Aucune donnée disponible</div>;
  }

  const formatDate = (dateStr: string | number) => {
    const date =
      typeof dateStr === "number" ? new Date(dateStr) : new Date(dateStr);
    return format(date, "dd MMMM yyyy", { locale: fr });
  };

  const getTypeLabel = (typeCode: number) => {
    switch (typeCode) {
      case 380:
        return "Facture";
      case 381:
        return "Avoir";
      case 384:
        return "Facture de correction";
      default:
        return `Type ${typeCode}`;
    }
  };

  const getStateLabel = (state: ReceivedEInvoices["state"]) => {
    switch (state) {
      case "new":
        return { label: "Nouveau", color: "blue" as const };
      case "attached":
        return { label: "Rattaché", color: "green" as const };
      case "rejected":
        return { label: "Ignorée", color: "red" as const };
    }
  };

  const stateInfo = getStateLabel(invoice.state);

  // Document-level allowances and charges (BG-20 / BG-21), e.g. shipping or
  // eco-participation: they are part of the total but not of the lines
  const amount = (value?: string) => parseFloat(value || "0") || 0;
  const linesTotal = _.sumBy(enInvoice.lines, (line) =>
    amount(line.net_amount),
  );
  const allowances = enInvoice.document_level_allowances || [];
  const charges = enInvoice.charges || [];
  const computedTotal =
    linesTotal -
    _.sumBy(allowances, (a) => amount(a.amount)) +
    _.sumBy(charges, (c) => amount(c.amount));
  const totalGap =
    Math.round((invoice.total_amount - computedTotal) * 100) / 100;

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              {enInvoice.number}
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              {getTypeLabel(invoice.type_code)}
            </p>
          </div>
          <Badge color={stateInfo.color}>{stateInfo.label}</Badge>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-slate-600 dark:text-slate-400">
              Date d'émission:
            </span>
            <p className="font-medium">{formatDate(enInvoice.issue_date)}</p>
          </div>
          {enInvoice.payment_due_date && (
            <div>
              <span className="text-slate-600 dark:text-slate-400">
                Date d'échéance:
              </span>
              <p className="font-medium">
                {formatDate(enInvoice.payment_due_date)}
              </p>
            </div>
          )}
          <div>
            <span className="text-slate-600 dark:text-slate-400">Devise:</span>
            <p className="font-medium">{invoice.currency_code}</p>
          </div>
          {enInvoice.buyer_reference && (
            <div>
              <span className="text-slate-600 dark:text-slate-400">
                Référence acheteur:
              </span>
              <p className="font-medium">{enInvoice.buyer_reference}</p>
            </div>
          )}
        </div>
      </div>

      {/* Parties Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Seller */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-lg font-semibold mb-3 text-slate-900 dark:text-slate-100">
            Fournisseur
          </h3>
          <div className="space-y-2 text-sm">
            <p className="font-medium">{enInvoice.seller.name}</p>
            {enInvoice.seller.vat_identifier && (
              <p className="text-slate-600 dark:text-slate-400">
                TVA: {enInvoice.seller.vat_identifier}
              </p>
            )}
            {enInvoice.seller.postal_address && (
              <div className="text-slate-600 dark:text-slate-400">
                {enInvoice.seller.postal_address.address_line1 && (
                  <p>{enInvoice.seller.postal_address.address_line1}</p>
                )}
                {enInvoice.seller.postal_address.address_line2 && (
                  <p>{enInvoice.seller.postal_address.address_line2}</p>
                )}
                <p>
                  {enInvoice.seller.postal_address.post_code}{" "}
                  {enInvoice.seller.postal_address.city}
                </p>
                {enInvoice.seller.postal_address.country_code && (
                  <p>{enInvoice.seller.postal_address.country_code}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Buyer */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-lg font-semibold mb-3 text-slate-900 dark:text-slate-100">
            Acheteur
          </h3>
          <div className="space-y-2 text-sm">
            <p className="font-medium">{enInvoice.buyer.name}</p>
            {enInvoice.buyer.vat_identifier && (
              <p className="text-slate-600 dark:text-slate-400">
                TVA: {enInvoice.buyer.vat_identifier}
              </p>
            )}
            {enInvoice.buyer.postal_address && (
              <div className="text-slate-600 dark:text-slate-400">
                {enInvoice.buyer.postal_address.address_line1 && (
                  <p>{enInvoice.buyer.postal_address.address_line1}</p>
                )}
                {enInvoice.buyer.postal_address.address_line2 && (
                  <p>{enInvoice.buyer.postal_address.address_line2}</p>
                )}
                <p>
                  {enInvoice.buyer.postal_address.post_code}{" "}
                  {enInvoice.buyer.postal_address.city}
                </p>
                {enInvoice.buyer.postal_address.country_code && (
                  <p>{enInvoice.buyer.postal_address.country_code}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invoice Lines */}
      <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Lignes de facturation
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase text-slate-700 dark:text-slate-300">
              <tr>
                <th className="px-6 py-3 text-left">Description</th>
                <th className="px-6 py-3 text-right">Quantité</th>
                <th className="px-6 py-3 text-right">Prix unitaire HT</th>
                <th className="px-6 py-3 text-right">TVA</th>
                <th className="px-6 py-3 text-right">Total HT</th>
                <th className="px-6 py-3 text-right">Total TTC</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {enInvoice.lines.map((line, index) => (
                <tr
                  key={index}
                  className="hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        {line.item_information.name}
                      </p>
                      {line.item_information.description && (
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                          {line.item_information.description}
                        </p>
                      )}
                      {line.item_information.seller_identifier && (
                        <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                          Réf: {line.item_information.seller_identifier}
                        </p>
                      )}
                      {(line.allowances || []).map((a, i) => (
                        <p
                          key={"a" + i}
                          className="text-xs text-slate-500 dark:text-slate-500 mt-1"
                        >
                          Remise {a.reason || a.reason_code || ""} : -
                          {formatAmount(a.amount, invoice.currency_code)}
                        </p>
                      ))}
                      {(line.charges || []).map((c, i) => (
                        <p
                          key={"c" + i}
                          className="text-xs text-slate-500 dark:text-slate-500 mt-1"
                        >
                          Frais {c.reason || c.reason_code || ""} : +
                          {formatAmount(c.amount, invoice.currency_code)}
                        </p>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right whitespace-nowrap">
                    {formatNumber(line.invoiced_quantity)}{" "}
                    <Unit
                      unit={normalizeUnitCode(line.invoiced_quantity_code)}
                    />
                  </td>
                  <td className="px-6 py-4 text-right">
                    {formatAmount(
                      // The net price is given for a base quantity (BT-149)
                      parseFloat(line.price_details.item_net_price) /
                        (parseFloat(
                          line.price_details.item_price_base_quantity || "1",
                        ) || 1),
                      invoice.currency_code,
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {line.vat_information.invoiced_item_vat_rate
                      ? `${line.vat_information.invoiced_item_vat_rate}%`
                      : line.vat_information.invoiced_item_vat_category_code}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {formatAmount(line.net_amount, invoice.currency_code)}
                  </td>
                  <td className="px-6 py-4 text-right font-medium">
                    {formatAmount(
                      line.line_with_vat_net_amount ||
                        parseFloat(line.net_amount) *
                          (1 +
                            parseFloat(
                              line.vat_information.invoiced_item_vat_rate ||
                                "0",
                            ) /
                              100),
                      invoice.currency_code,
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals Section */}
      <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
        <div className="max-w-md ml-auto space-y-3">
          {(allowances.length > 0 || charges.length > 0) && (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">
                  Total des lignes HT:
                </span>
                <span className="font-medium">
                  {formatAmount(linesTotal, invoice.currency_code)}
                </span>
              </div>
              {allowances.map((a, i) => (
                <div key={"a" + i} className="flex justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400">
                    Remise {a.reason || a.reason_code || ""} (TVA{" "}
                    {a.vat_rate ? `${a.vat_rate}%` : a.vat_category_code}):
                  </span>
                  <span className="font-medium">
                    -{formatAmount(a.amount, invoice.currency_code)}
                  </span>
                </div>
              ))}
              {charges.map((c, i) => (
                <div key={"c" + i} className="flex justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400">
                    {c.reason || c.reason_code || "Frais"} (TVA{" "}
                    {c.vat_rate ? `${c.vat_rate}%` : c.vat_category_code}):
                  </span>
                  <span className="font-medium">
                    +{formatAmount(c.amount, invoice.currency_code)}
                  </span>
                </div>
              ))}
            </>
          )}
          {Math.abs(totalGap) >= 0.01 && (
            <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md text-xs text-amber-800 dark:text-amber-200">
              <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>
                {formatAmount(Math.abs(totalGap), invoice.currency_code)} HT{" "}
                {totalGap > 0 ? "de frais" : "de remise"} ne sont détaillés dans
                aucune ligne, remise ou frais transmis : ils seront repris sur
                une ligne dédiée du document créé.
              </p>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-slate-600 dark:text-slate-400">
              Total HT:
            </span>
            <span className="font-medium">
              {formatAmount(invoice.total_amount, invoice.currency_code)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600 dark:text-slate-400">TVA:</span>
            <span className="font-medium">
              {formatAmount(invoice.total_tax_amount, invoice.currency_code)}
            </span>
          </div>
          <div className="flex justify-between text-lg font-bold pt-3 border-t border-slate-200 dark:border-slate-700">
            <span>Total TTC:</span>
            <span>
              {formatAmount(
                invoice.total_amount_with_tax,
                invoice.currency_code,
              )}
            </span>
          </div>
        </div>
      </div>

      {/* VAT Breakdown */}
      {enInvoice.vat_break_down && enInvoice.vat_break_down.length > 0 && (
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-slate-100">
            Détail de la TVA
          </h3>
          <div className="space-y-2">
            {enInvoice.vat_break_down.map((vat, index) => (
              <div
                key={index}
                className="flex justify-between text-sm py-2 border-b border-slate-100 dark:border-slate-800 last:border-0"
              >
                <span className="text-slate-600 dark:text-slate-400">
                  TVA{" "}
                  {vat.vat_category_rate
                    ? `${vat.vat_category_rate}%`
                    : vat.vat_category_code}
                </span>
                <div className="text-right">
                  <div className="font-medium">
                    {formatNumber(parseFloat(vat.vat_category_tax_amount))}{" "}
                    {invoice.currency_code}
                  </div>
                  <div className="text-xs text-slate-500">
                    sur{" "}
                    {formatNumber(parseFloat(vat.vat_category_taxable_amount))}{" "}
                    {invoice.currency_code}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Information */}
      {(enInvoice.payment_terms || enInvoice.payment_instructions) && (
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-slate-100">
            Informations de paiement
          </h3>
          <div className="space-y-2 text-sm">
            {enInvoice.payment_terms && (
              <div>
                <span className="text-slate-600 dark:text-slate-400">
                  Conditions:
                </span>
                <p className="font-medium">{enInvoice.payment_terms}</p>
              </div>
            )}
            {!!enInvoice.payment_instructions?.credit_transfers?.length && (
              <div>
                <span className="text-slate-600 dark:text-slate-400">
                  Virement bancaire:
                </span>
                {enInvoice.payment_instructions.credit_transfers.map(
                  (transfer, index) => (
                    <p key={index} className="font-mono text-xs mt-1">
                      {transfer.payment_account_identifier.value}
                    </p>
                  ),
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notes */}
      {enInvoice.notes && enInvoice.notes.length > 0 && (
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-slate-100">
            Notes
          </h3>
          <div className="space-y-2">
            {enInvoice.notes.map((note, index) => (
              <p
                key={index}
                className="text-sm text-slate-600 dark:text-slate-400"
              >
                {note.note}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
